import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import FixedAssetTable from "~/components/fixed-asset/table/fixed-asset-table";
import { IconEllipsisVertical, IconPlus } from "~/components/icons";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  businessDetailResource,
  businessesResource,
  useActiveBusinessId,
} from "~/features/business/business.resource";
import { getFixedAssetsPageData } from "~/features/fixed-assets/fixed-assets-page";
import { FixedAssetsResource } from "~/features/fixed-assets/fixed-assets.resource";
import { withBusinessGuard } from "~/lib/route-guards";

export const Route = createFileRoute("/_dashboard/dashboard/fixed-assets/")({
  ...withBusinessGuard(),
  loader: async ({ context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;
    const data = await getFixedAssetsPageData({ data: { businessId } });
    // seedIfEmpty (not seed) so a fresh client-side mutation (e.g. a rename
    // that optimistically updated the list cache) isn't clobbered by stale
    // loader data when navigating back to the table.
    FixedAssetsResource.seedIfEmpty(
      context.queryClient,
      { businessId, limit: 20 },
      data.assets,
    );
    businessDetailResource.seed(
      context.queryClient,
      businessId,
      data.businessDetail,
    );
    return {
      businessId,
      assets: data.assets,
      activeBusiness: data.businessDetail,
      collection,
    };
  },
  component: FixedAssetsPage,
});

function FixedAssetsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const data = Route.useLoaderData();

  businessDetailResource.seedIfEmpty(
    qc,
    data().businessId,
    data().activeBusiness,
  );
  FixedAssetsResource.seedIfEmpty(
    qc,
    { businessId: data().businessId, limit: 20 },
    data().assets,
  );

  const businessId = useActiveBusinessId();

  const businessQuery = createQuery(() => ({
    ...businessDetailResource.options(businessId() ?? "", !!businessId()),
    placeholderData: keepPreviousData,
  }));

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 sm:px-6 pb-3 pt-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold">Fixed Assets</h1>
          <p class="text-muted-foreground">
            Manage your fixed assets and depreciation schedules.
          </p>
        </div>
        <DropdownMenu placement="bottom-end">
          <DropdownMenuTrigger
            as={Button<"button">}
            variant="outline"
            size="icon"
          >
            <IconEllipsisVertical class="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent class="w-48">
            <DropdownMenuItem
              onClick={() =>
                navigate({
                  to: "/dashboard/fixed-assets/$fixedAssetId",
                  params: { fixedAssetId: "new" },
                })
              }
            >
              <IconPlus class="size-4" />
              Add Fixed Asset
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <FixedAssetTable businessId={businessId() ?? ""} />
    </div>
  );
}
